import { Box, BoxProps, Circle, Grid, GridItem, HStack, IconButton, Text, useDisclosure } from "@chakra-ui/react";
import { StatusCode } from "grpc-web";
import { RefObject, useCallback, useContext, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FaPencilAlt, FaTimes, FaUndo } from "react-icons/fa";

// Components
import { DocLink } from "../../pages/claims/NewClaim.styles";
import { SecondaryButton } from "../Button/index.styles";
import FileOperationProgress, { FileOperationItem } from "../FileOperationProgress";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import CustomModal from "../Modals/CustomModal";

// Hooks
import useClaimsToast from "../../hooks/useToast";
import { useAuth } from "../../hooks/useAuth";

// Providers
import { ClaimContext, ClaimContextType } from "../../context-providers/claims-provider";

// Protos
import { DocumentCategory as DocumentCategoryRPC } from "../../utils/api/protos/messages/claims.message_pb";

// Utils
import { debounce, handleDocClick, validateFileSize, validateClaimSize, formatFileSize } from "../../utils/helpers";

// Types
import { ClaimStatus, DocumentCategory, FILE_SIZE_LIMITS, UserRole } from "../../utils/consts";
import { Claim, ClaimDocument } from "../../utils/types";

interface DataCardDisplayDocsProps {
	/** A manually provided claim just used for display */
	claim?: Claim;
	style?: BoxProps;
	showEditButton?: boolean;
	useExternalEditButton?: boolean;
	buttonRef?: RefObject<HTMLButtonElement>;
	isPermanentDeleteEnabled?: boolean;
	isProvider: boolean;
}

// Helper function to safely use context
const useSafeClaimContext = (): ClaimContextType => {
	const context = useContext(ClaimContext);
	if (!context) {
		throw new Error("useSafeClaimContext must be used within a ClaimProvider");
	}
	return context;
};

// Helper function to check if the claim status allows document modifications
const isClaimEditable = (status: ClaimStatus): boolean => {
	// Allow document modifications in Draft status and all review statuses
	return (
		status === ClaimStatus.Draft ||
		status === ClaimStatus.InReview ||
		status === ClaimStatus.PendingVerification ||
		status === ClaimStatus.PendingProvider ||
		status === ClaimStatus.PendingInternalReview
	);
};

const DataCardDisplayDocs = (props: DataCardDisplayDocsProps) => {
	const {
		claim,
		style = { alignItems: "flex-start", textAlign: "left" },
		showEditButton = false,
		useExternalEditButton = false,
		buttonRef,
		isPermanentDeleteEnabled = false,
		isProvider
	} = props;
	const { isOpen, onOpen, onClose } = useDisclosure();
	const claimContext = useSafeClaimContext();

	// Enhanced state for tracking file downloads and uploads with better progress management
	const [downloadOperations, setDownloadOperations] = useState<FileOperationItem[]>([]);
	const [uploadOperations, setUploadOperations] = useState<FileOperationItem[]>([]);

	// Extract documents directly from the claim context
	// What is the return type of this?
	const documents = useMemo(() => {
		const claimDocuments = claim?.documents ?? claimContext?.claim?.documents;
		if (!claimDocuments) {
			return {
				invoices: [],
				supportingDocuments: [],
				claimForms: [],
				additionalDocuments: [],
				ccs: [],
				deniedDocuments: [],
				auditDocuments: []
			};
		}

		// Extract and categorize documents from the claim data
		// Might be a good idea to just loop once for O (log n) instead of doing it 5 times...
		// Also might be a good idea to use types in typescript instead of anonymous inlines...
		const invoices = claimDocuments
			.filter((doc) => doc.category === DocumentCategory.Invoices)
			.map((doc) => {
				console.log(
					`[Document Display] Invoice: ${doc.file?.fileName || "Unknown"}, Size: ${doc.fileSize || 0} bytes (${doc.fileSize ? (doc.fileSize / 1024 / 1024).toFixed(2) : "0"} MB)`
				);
				return {
					fileId: doc.file?.fileId || "",
					fileName: doc.file?.fileName || "",
					isDeleted: doc.isDeleted,
					fileSize: doc.fileSize,
					isGenerated: doc.isGenerated || false
				};
			});

		const supportingDocuments = claimDocuments
			.filter((doc) => doc.category === DocumentCategory.SupportingDocuments)
			.map((doc) => {
				console.log(
					`[Document Display] Supporting Document: ${doc.file?.fileName || "Unknown"}, Size: ${doc.fileSize || 0} bytes (${doc.fileSize ? (doc.fileSize / 1024 / 1024).toFixed(2) : "0"} MB)`
				);
				return {
					fileId: doc.file?.fileId || "",
					fileName: doc.file?.fileName || "",
					isDeleted: doc.isDeleted,
					fileSize: doc.fileSize,
					isGenerated: doc.isGenerated || false
				};
			});

		const claimForms = claimDocuments
			.filter((doc) => doc.category === DocumentCategory.ClaimForms)
			.map((doc) => {
				console.log(
					`[Document Display] Claim Form: ${doc.file?.fileName || "Unknown"}, Size: ${doc.fileSize || 0} bytes (${doc.fileSize ? (doc.fileSize / 1024 / 1024).toFixed(2) : "0"} MB)`
				);
				return {
					fileId: doc.file?.fileId || "",
					fileName: doc.file?.fileName || "",
					isDeleted: doc.isDeleted,
					fileSize: doc.fileSize,
					isGenerated: doc.isGenerated || false
				};
			});

		console.log("[DataDisplayDocs] Claimdocuments >>> ", claimDocuments);
		const ccs = claimDocuments
			.filter((doc) => {
				const isCcs = doc.category === DocumentCategory.Ccs;
				console.log(
					`[DataDisplayDocs] Checking doc category: "${doc.category}" === "${DocumentCategory.Ccs}" ?`,
					doc.category === DocumentCategory.Ccs,
					'Also checking literal "Ccs":',
					doc.category === "Ccs",
					"Final result:",
					isCcs,
					doc
				);
				return isCcs;
			})
			.map((doc) => {
				console.log(
					`[Document Display] Ccs: ${doc.file?.fileName || "Unknown"}, Size: ${doc.fileSize || 0} bytes (${doc.fileSize ? (doc.fileSize / 1024 / 1024).toFixed(2) : "0"} MB), isGenerated: ${doc.isGenerated}`
				);
				return {
					fileId: doc.file?.fileId || "",
					fileName: doc.file?.fileName || "",
					isDeleted: doc.isDeleted,
					fileSize: doc.fileSize,
					isGenerated: doc.isGenerated
				};
			});

		console.log("[DataDisplayDocs] CCS documents after filter:", ccs);

		const additionalDocuments = claimDocuments
			.filter((doc) => doc.category === DocumentCategory.ProofOfFullTimeSchoolEnrollment)
			.map((doc) => {
				console.log(
					`[Document Display] Additional Document: ${doc.file?.fileName || "Unknown"}, Size: ${doc.fileSize || 0} bytes (${doc.fileSize ? (doc.fileSize / 1024 / 1024).toFixed(2) : "0"} MB)`
				);
				return {
					fileId: doc.file?.fileId || "",
					fileName: doc.file?.fileName || "",
					isDeleted: doc.isDeleted,
					fileSize: doc.fileSize,
					isGenerated: doc.isGenerated || false
				};
			});

		const auditDocuments = claimDocuments
			.filter((doc) => doc.category === DocumentCategory.AuditDocuments)
			.map((doc) => {
				console.log(
					`[Document Display] Audit Document: ${doc.file?.fileName || "Unknown"}, Size: ${doc.fileSize || 0} bytes (${doc.fileSize ? (doc.fileSize / 1024 / 1024).toFixed(2) : "0"} MB)`
				);
				return {
					fileId: doc.file?.fileId || "",
					fileName: doc.file?.fileName || "",
					isDeleted: doc.isDeleted,
					fileSize: doc.fileSize,
					isGenerated: doc.isGenerated || false
				};
			});

		const deniedDocuments = claimDocuments
			.filter((doc) => doc.category === DocumentCategory.Denied)
			.map((doc) => {
				console.log(
					`[Document Display] Denied Document: ${doc.file?.fileName || "Unknown"}, Size: ${doc.fileSize || 0} bytes (${doc.fileSize ? (doc.fileSize / 1024 / 1024).toFixed(2) : "0"} MB)`
				);
				return {
					fileId: doc.file?.fileId || "",
					fileName: doc.file?.fileName || "",
					isDeleted: doc.isDeleted,
					fileSize: doc.fileSize
				};
			});

		return {
			invoices,
			supportingDocuments,
			claimForms,
			additionalDocuments,
			ccs,
			deniedDocuments,
			auditDocuments
		};
	}, [claim, claimContext.claim?.documents]);

	const { invoices, supportingDocuments, claimForms, additionalDocuments, ccs, deniedDocuments, auditDocuments } =
		documents;
	const displayClaim = claim ?? claimContext?.claim;
	const isHeldForAudit = displayClaim?.isHeldForAudit === true;
	const canUploadCategory = useCallback(
		(category: DocumentCategoryRPC): boolean => !isHeldForAudit || category === DocumentCategoryRPC.AUDIT_DOCUMENTS,
		[isHeldForAudit]
	);

	// Wrap pendingDeletions in useMemo to avoid dependency warnings
	const pendingDeletions = useMemo(() => {
		return claimContext?.pendingDeletions || new Set<string>();
	}, [claimContext?.pendingDeletions]);

	const toast = useClaimsToast();
	const { getUser } = useAuth();
	const currentUser = getUser();

	// Determine if the claim is editable based on its status
	const editable = claimContext?.claim?.status ? isClaimEditable(claimContext.claim.status) : false;

	// Enhanced document upload handler with progress tracking
	const handleDocumentUpload = useCallback(
		async (acceptedFiles: Array<File>, category: DocumentCategoryRPC, categoryName: string) => {
			if (!claimContext?.handleDocumentAssociation || !claimContext?.claim?.claimId) {
				toast("Error", "Document upload is not available in this context", "error");
				return;
			}

			if (!canUploadCategory(category)) {
				toast("Error", "Only audit documents can be uploaded while this claim is held for audit", "error");
				return;
			}

			// Check if claim is editable based on status
			if (!isClaimEditable(claimContext.claim.status)) {
				toast(
					"Error",
					`Documents can only be uploaded to claims in "Draft" or Review statuses. This claim is in "${claimContext.claim.status}" status.`,
					"error"
				);
				return;
			}

			if (acceptedFiles.length === 0) {
				return;
			}

			// Validate file sizes before processing
			for (const file of acceptedFiles) {
				const fileValidation = validateFileSize(file.size, file.name);
				if (!fileValidation.isValid) {
					toast("File Size Error", fileValidation.error!, "error");
					return;
				}
			}

			// Validate total claim size
			const totalNewSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0);
			const claimValidation = validateClaimSize(claimContext.claim.documents, totalNewSize);
			if (!claimValidation.isValid) {
				toast("Claim Size Error", claimValidation.error!, "error");
				return;
			}

			claimContext.setIsLoading(true);

			// Create initial upload operations for all files
			const newOperations: FileOperationItem[] = acceptedFiles.map((file) => ({
				id: `${file.name}-${Date.now()}-${Math.random()}`,
				fileName: file.name,
				progress: 0,
				status: "queued"
			}));

			setUploadOperations((prev) => [...prev, ...newOperations]);

			// Process each file - use for...of loop instead of forEach to properly handle async operations
			for (let index = 0; index < acceptedFiles.length; index++) {
				const file = acceptedFiles[index];
				const operationId = newOperations[index].id;
				// Capture the category in a local variable to ensure it's preserved
				const fileCategory = category;

				try {
					// Update status to processing
					setUploadOperations((prev) =>
						prev.map((op) => (op.id === operationId ? { ...op, status: "processing" as const } : op))
					);

					// Log the category being uploaded for debugging
					console.log(`[DataDisplayDocs] Uploading file "${file.name}" with category:`, fileCategory);

					// Upload the file and track progress
					await claimContext.handleDocumentAssociation(null, file, fileCategory, (progress: number) => {
						setUploadOperations((prev) => prev.map((op) => (op.id === operationId ? { ...op, progress } : op)));
					});

					// Log after successful upload
					console.log(`[DataDisplayDocs] Successfully uploaded file "${file.name}" with category:`, fileCategory);

					// Mark as completed
					setUploadOperations((prev) =>
						prev.map((op) => (op.id === operationId ? { ...op, status: "completed" as const, progress: 100 } : op))
					);

					// Remove completed operation after a delay
					setTimeout(() => {
						setUploadOperations((prev) => prev.filter((op) => op.id !== operationId));
					}, 3000);

					// Refresh the claim data to ensure UI is up to date
					if (claimContext?.claim?.claimId) {
						claimContext.handleFetchClaim(claimContext.claim.claimId).catch((err) => {
							console.error("Error refreshing claim after document upload:", err);
						});
					}
				} catch (error) {
					console.error("Error uploading file:", error);
					const errorMessage = error instanceof Error ? error.message : "Upload failed";

					// Mark as failed
					setUploadOperations((prev) =>
						prev.map((op) => (op.id === operationId ? { ...op, status: "failed" as const, error: errorMessage } : op))
					);

					toast("Error", `Failed to upload ${file.name}: ${errorMessage}`, "error");
				}
			}

			// Check if all uploads are complete
			setTimeout(() => {
				setUploadOperations((prev) => {
					const remaining = prev.filter((op) => op.status === "processing" || op.status === "queued");
					if (remaining.length === 0) {
						claimContext.setIsLoading(false);
					}
					return prev;
				});
			}, 1000);
		},
		[canUploadCategory, claimContext, toast]
	);

	// Specific handlers for each document type
	const handleInvoicesUpload = useCallback(
		(acceptedFiles: Array<File>) => {
			return handleDocumentUpload(acceptedFiles, DocumentCategoryRPC.INVOICES, "invoice");
		},
		[handleDocumentUpload]
	);

	const handleSupportingDocsUpload = useCallback(
		(acceptedFiles: Array<File>) => {
			return handleDocumentUpload(acceptedFiles, DocumentCategoryRPC.SUPPORTING_DOCUMENTS, "supporting document");
		},
		[handleDocumentUpload]
	);

	const handleClaimFormsUpload = useCallback(
		(acceptedFiles: Array<File>) => {
			// Use numeric value 3 directly since DocumentCategoryRPC.CLAIM_FORMS has a syntax error in protobuf
			return handleDocumentUpload(acceptedFiles, 3 as DocumentCategoryRPC, "claim form");
		},
		[handleDocumentUpload]
	);

	const handleAdditionalDocsUpload = useCallback(
		(acceptedFiles: Array<File>) => {
			return handleDocumentUpload(
				acceptedFiles,
				DocumentCategoryRPC.PROOF_OF_FULL_TIME_SCHOOL_ENROLLMENT,
				"proof of full-time school enrollment"
			);
		},
		[handleDocumentUpload]
	);

	const handleDeniedDocsUpload = useCallback(
		(acceptedFiles: Array<File>) => {
			return handleDocumentUpload(acceptedFiles, DocumentCategoryRPC.DENIED_DOCUMENTS, "Denied");
		},
		[handleDocumentUpload]
	);

	const handleAuditDocsUpload = useCallback(
		(acceptedFiles: Array<File>) => {
			return handleDocumentUpload(acceptedFiles, DocumentCategoryRPC.AUDIT_DOCUMENTS, "audit document");
		},
		[handleDocumentUpload]
	);

	const {
		getRootProps: getRootPropsInvoices,
		getInputProps: getInputPropsInvoices,
		isDragActive: isDragActiveInvoices,
		open: openInvoices
	} = useDropzone({
		onDrop: handleInvoicesUpload,
		multiple: true,
		noClick: true,
		noKeyboard: true
	});

	const {
		getRootProps: getRootPropsSupportingDocuments,
		getInputProps: getInputPropsSupportingDocuments,
		isDragActive: isDragActiveSupportingDocuments,
		open: openSupportingDocuments
	} = useDropzone({
		onDrop: handleSupportingDocsUpload,
		multiple: true,
		noClick: true,
		noKeyboard: true
	});

	const {
		getRootProps: getRootPropsClaimForms,
		getInputProps: getInputPropsClaimForms,
		isDragActive: isDragActiveClaimForms,
		open: openClaimForms
	} = useDropzone({
		onDrop: handleClaimFormsUpload,
		multiple: true,
		noClick: true,
		noKeyboard: true
	});

	const {
		getRootProps: getRootPropsProofOfFullTimeSchool,
		getInputProps: getInputPropsProofOfFullTimeSchool,
		isDragActive: isDragActiveProofOfFullTimeSchool,
		open: openProofOfFullTimeSchool
	} = useDropzone({
		onDrop: handleAdditionalDocsUpload,
		multiple: true,
		noClick: true,
		noKeyboard: true
	});

	const {
		getRootProps: getRootPropsDeniedDocs,
		getInputProps: getInputPropsDeniedDocs,
		isDragActive: isDragActiveDeniedDocs,
		open: openDeniedDocs
	} = useDropzone({
		onDrop: handleDeniedDocsUpload,
		multiple: true,
		noClick: true,
		noKeyboard: true
	});

	const {
		getRootProps: getRootPropsAuditDocs,
		getInputProps: getInputPropsAuditDocs,
		isDragActive: isDragActiveAuditDocs,
		open: openAuditDocs
	} = useDropzone({
		onDrop: handleAuditDocsUpload,
		multiple: true,
		noClick: true,
		noKeyboard: true
	});

	// Enhanced download handler with progress tracking and loading screen
	const handleEnhancedDocClick = useCallback(
		async (fileId: string, fileName: string) => {
			// Prevent multiple downloads
			if (claimContext.isLoading) return;

			claimContext.setIsLoading(true);

			// Create a download operation entry
			const operationId = `${fileId}-${Date.now()}-${Math.random()}`;
			const newOperation: FileOperationItem = {
				id: operationId,
				fileName,
				progress: 0,
				status: "processing"
			};

			setDownloadOperations((prev) => [...prev, newOperation]);

			try {
				// Call handleDocClick with progress callback
				await handleDocClick(
					fileId,
					toast,
					true, // openNewTab
					undefined, // downloadFileName
					(progress: number) => {
						// Update progress
						setDownloadOperations((prev) => prev.map((op) => (op.id === operationId ? { ...op, progress } : op)));
					}
				);

				// Mark as completed
				setDownloadOperations((prev) =>
					prev.map((op) => (op.id === operationId ? { ...op, status: "completed" as const, progress: 100 } : op))
				);

				// Remove completed operation after a delay
				setTimeout(() => {
					setDownloadOperations((prev) => prev.filter((op) => op.id !== operationId));
				}, 3000);
			} catch (error) {
				console.error("Error downloading file:", error);
				const errorMessage = error instanceof Error ? error.message : "Download failed";

				// Mark as failed
				setDownloadOperations((prev) =>
					prev.map((op) => (op.id === operationId ? { ...op, status: "failed" as const, error: errorMessage } : op))
				);
			} finally {
				claimContext.setIsLoading(false);
			}
		},
		[toast, claimContext]
	);

	// Raw handler function with useCallback
	const handleDeleteDocClickRaw = useCallback(
		async (fileId: string, isDeleted: boolean | undefined, isPermanent: boolean = false) => {
			// Make sure context is available
			if (!claimContext) {
				console.error("Claims context not available for document deletion");
				toast("Error", "Unable to access claim data", "error");
				return;
			}

			// Check if claim exists and has valid data
			if (!claimContext.claim?.claimId) {
				console.error("No claim ID available for document deletion");
				toast("Error", "Claim data is not available", "error");
				return;
			}

			// Check if the operation is already in progress
			if (pendingDeletions.has(fileId)) {
				toast("Info", "Operation already in progress. Please wait.", "info");
				return;
			}

			// Find the document in the claim data
			const document = claimContext.claim.documents.find((doc: ClaimDocument) => doc.file?.fileId === fileId);
			if (!document) {
				console.error(`Document with ID ${fileId} not found in claim data`);
				toast("Error", "Document not found in claim data", "error");
				return;
			}

			// Use claim ID from context
			const claimId = claimContext.claim.claimId;

			// Determine the operation type and create a unique toast ID
			const operationType = document.isDeleted ? "recover" : isPermanent ? "permanently delete" : "delete";

			// Create a more unique toast ID by adding a timestamp and random component
			// This ensures that even rapid operations on the same file won't create duplicate keys
			const uniqueTimestamp = Date.now();
			const randomComponent = Math.random().toString(36).substring(2, 8);
			const loadingToastId = `doc-${operationType}-${fileId}-${uniqueTimestamp}-${randomComponent}-loading`;

			// Get the category name for display
			const categoryName = document.category || "Unknown";

			// Show a loading notification with category instead of filename
			toast(
				"Processing",
				`${operationType === "recover" ? "Recovering" : isPermanent ? "Permanently deleting" : "Deleting"} file in ${categoryName}...`,
				"loading",
				30000,
				loadingToastId
			);

			try {
				// Call the appropriate function based on document.isDeleted status and isPermanent flag
				let result;
				if (document.isDeleted) {
					result = await claimContext.handleRecoverDocument(claimId, fileId);
				} else if (isPermanent) {
					result = await claimContext.handlePermanentlyRemoveDocument(claimId, fileId);
				} else {
					result = await claimContext.handleRemoveDocument(claimId, fileId);
				}

				// Handle the response
				if (result.success) {
					// First close the loading toast
					if (toast) {
						toast("", "", "loading", 1, loadingToastId);
					}

					// Use a separate toast ID for the success message to ensure it's not dismissed with the loading toast
					// Adding the same unique components to ensure no key collisions
					const successToastId = `doc-${operationType}-${fileId}-${uniqueTimestamp}-${randomComponent}-success`;

					// Show success message
					toast(
						"Success",
						`File ${operationType === "recover" ? "recovered" : isPermanent ? "permanently deleted" : "deleted"} successfully`,
						"success",
						3000,
						successToastId
					);

					// Refresh claim data to ensure UI is up to date
					if (claimContext && claimContext.claim?.claimId) {
						setTimeout(() => {
							claimContext.handleFetchClaim(claimContext.claim.claimId!).catch((err) => {
								console.error("Error refreshing claim after document operation:", err);
							});
						}, 500);
					}
				} else {
					// Operation failed with an error
					console.error("Error in document operation:", result.error);

					// Create a user-friendly error message based on the error code
					let errorTitle = "Error";
					let errorMessage = result.error?.message || "Unable to process document";
					let errorDuration = 5000;

					// Handle specific error codes with friendly messages
					if (result.error?.code === StatusCode.PERMISSION_DENIED) {
						errorTitle = "Permission Denied";
						errorMessage = "You don't have permission to modify this document.";
						errorDuration = 8000;
					} else if (result.error?.code === StatusCode.UNAUTHENTICATED) {
						errorTitle = "Session Expired";
						errorMessage = "Your session has expired. Please refresh the page and log in again.";
						errorDuration = 8000;
					} else if (result.error?.code === StatusCode.NOT_FOUND) {
						errorMessage = "The document could not be found.";
					} else if (result.error?.code === StatusCode.FAILED_PRECONDITION) {
						errorMessage = "This document cannot be modified in its current state.";
					}

					// First close the loading toast
					if (toast) {
						toast("", "", "loading", 1, loadingToastId);
					}

					// Use a separate toast ID for the error message with unique components
					const errorToastId = `doc-${operationType}-${fileId}-${uniqueTimestamp}-${randomComponent}-error`;

					// Show the error toast
					toast(errorTitle, errorMessage, "error", errorDuration, errorToastId);
				}
			} catch (error) {
				// This should rarely happen since document operations already handle errors
				console.error("Unexpected error in document action:", error);

				// First close the loading toast
				if (toast) {
					toast("", "", "loading", 1, loadingToastId);
				}

				// Use a separate toast ID for the error message with unique components
				const errorToastId = `doc-${operationType}-${fileId}-${uniqueTimestamp}-${randomComponent}-error`;

				toast("Error", "An unexpected error occurred. Please try again.", "error", 5000, errorToastId);
			}
		},
		[claimContext, pendingDeletions, toast]
	);

	// Create a debounced version of the handler with additional checks
	const handleDeleteDocClick = useMemo(
		() =>
			debounce((fileId: string, isDeleted: boolean | undefined, isPermanent: boolean = false) => {
				// Prevent clicks from queuing up while an operation is in progress
				if (pendingDeletions.has(fileId)) {
					return;
				}
				handleDeleteDocClickRaw(fileId, isDeleted, isPermanent);
			}, 300),
		[handleDeleteDocClickRaw, pendingDeletions]
	);

	// Document buttons with permission check
	const DocumentButtons = useCallback(
		({
			fileId,
			isDeleted,
			category,
			isGenerated
		}: {
			fileId: string;
			isDeleted?: boolean;
			category: string;
			isGenerated?: boolean;
		}) => {
			const deleteLabel = isDeleted ? "Recover document" : "Delete document";
			const isPending = pendingDeletions.has(fileId);

			// Use isPermanentDeleteEnabled prop instead of checking claim status
			const isPermanentDelete = isPermanentDeleteEnabled && !isDeleted;

			// Check if user is a Provider (not Claims Analyst or Claims Manager)
			const isProvider = currentUser?.role.includes(UserRole.Provider) ?? false;
			const isClaimsAnalystOrManager =
				currentUser?.role.includes(UserRole.ClaimsAnalyst) ||
				currentUser?.role.includes(UserRole.ClaimsManager) ||
				currentUser?.role.includes(UserRole.ClaimsAdmin);

			// Providers cannot delete auto-generated CCS forms
			const isAutoGeneratedCcsRestricted =
				category === "Ccs" && isGenerated === true && isProvider && !isClaimsAnalystOrManager;

			// Determine if this is the last non-deleted document in a category
			const isLastInCategory = () => {
				let categoryDocs: Array<{ fileId: string; fileName: string; isDeleted?: boolean }> = [];

				// Why is this comparing raw strings when we have enums?
				if (category === "Invoices") categoryDocs = invoices;
				else if (category === "SupportingDocuments") categoryDocs = supportingDocuments;
				else if (category === "ClaimForms") categoryDocs = claimForms;
				else if (category === "AdditionalDocuments") categoryDocs = additionalDocuments;
				else if (category === "Ccs") categoryDocs = ccs;
				else if (category === "Denied") categoryDocs = deniedDocuments;

				return categoryDocs.filter((doc) => !doc.isDeleted).length <= 1 && !isDeleted;
			};

			// Don't allow deleting the last document in Invoices, SupportingDocuments, or ClaimForms categories
			const isLastRequired =
				(category === "Invoices" || category === "SupportingDocuments" || category === "ClaimForms") &&
				isLastInCategory();

			// Get appropriate message based on category
			const getCategoryMessage = () => {
				if (category === "Invoices") return "Cannot delete the last invoice document";
				if (category === "SupportingDocuments") return "Cannot delete the last supporting document";
				if (isAutoGeneratedCcsRestricted) return "Providers cannot delete auto-generated Claim forms";
				return "Cannot delete the last document";
			};

			// Prevent action if the document is the only non-deleted document in its protected category OR is auto-generated CCS for Provider
			if (isLastRequired || isAutoGeneratedCcsRestricted) {
				return (
					<IconButton
						icon={
							<Circle size="18px" bg="gray.300" color="white">
								<FaTimes size="12px" />
							</Circle>
						}
						aria-label={deleteLabel}
						size="xs"
						variant="unstyled"
						opacity={0.5}
						title={getCategoryMessage()}
						isDisabled={true}
					/>
				);
			}

			return (
				<IconButton
					icon={
						isDeleted ? (
							<Circle size="18px" bg="orange.500" color="white">
								<FaUndo size="10px" />
							</Circle>
						) : (
							<Circle size="18px" bg="gray.500" color="white">
								<FaTimes size="12px" />
							</Circle>
						)
					}
					aria-label={deleteLabel}
					size="xs"
					variant="unstyled"
					onClick={() => handleDeleteDocClick(fileId, isDeleted, isPermanentDelete)}
					isLoading={isPending}
					title={isDeleted ? "Recover document" : isPermanentDelete ? "Permanently delete document" : "Delete document"}
				/>
			);
		},
		[
			invoices,
			supportingDocuments,
			claimForms,
			additionalDocuments,
			ccs,
			pendingDeletions,
			deniedDocuments,
			handleDeleteDocClick,
			claimContext,
			isPermanentDeleteEnabled,
			currentUser
		]
	);

	return (
		<>
			{/* Loading Screens */}
			{claimContext.isLoading && <LoadingSpinner />}

			{/* Enhanced Download Progress Display - Shows when modal is closed */}
			{!isOpen && <FileOperationProgress operations={downloadOperations} operationType="download" />}

			<Box bg="offWhite" borderRadius="5px" px={4} py={2} textAlign="left" {...style}>
				<HStack justifyContent="space-between" mb={2} width="100%">
					{/* Only show edit button if claim is in Draft status */}
					{showEditButton && !useExternalEditButton && editable && (
						<IconButton
							aria-label="Edit Documents"
							icon={<FaPencilAlt />}
							size="sm"
							onClick={onOpen}
							colorScheme="blue"
							variant="ghost"
						/>
					)}

					{useExternalEditButton && editable && (
						<IconButton
							ref={buttonRef}
							aria-label="Edit Documents"
							icon={<FaPencilAlt />}
							size="sm"
							onClick={onOpen}
							colorScheme="blue"
							variant="ghost"
							display="none"
						/>
					)}
				</HStack>

				<Grid templateColumns="repeat(3, 1fr)" gap="20px">
					{/* Invoices Section */}
					{invoices.filter((doc) => !doc.isDeleted).length > 0 && (
						<GridItem>
							<Text fontWeight="600" color="gray.800" fontSize="16px" mb="8px" textAlign="left" width="100%">
								Invoices
							</Text>
							{invoices
								.filter((doc) => !doc.isDeleted)
								.map((doc) => (
									<HStack key={doc.fileId} alignItems="center" mb={1} justifyContent="flex-start" width="100%">
										<DocLink onClick={() => handleEnhancedDocClick(doc.fileId, doc.fileName)}>{doc.fileName}</DocLink>
									</HStack>
								))}
						</GridItem>
					)}

					{/* Supporting Documents Section */}
					{supportingDocuments.filter((doc) => !doc.isDeleted).length > 0 && (
						<GridItem>
							<Text fontWeight="600" color="gray.800" fontSize="16px" mb="8px" textAlign="left" width="100%">
								Supporting Documents
							</Text>
							{supportingDocuments
								.filter((doc) => !doc.isDeleted)
								.map((doc) => (
									<HStack key={doc.fileId} alignItems="center" mb={1} justifyContent="flex-start" width="100%">
										<DocLink onClick={() => handleEnhancedDocClick(doc.fileId, doc.fileName)}>{doc.fileName}</DocLink>
									</HStack>
								))}
						</GridItem>
					)}

					{/* Claim Forms Section (includes CCS) */}
					{[...claimForms, ...ccs].filter((doc) => !doc.isDeleted).length > 0 && (
						<GridItem>
							<Text fontWeight="600" color="gray.800" fontSize="16px" mb="8px" textAlign="left" width="100%">
								Claim Forms
							</Text>
							{[...claimForms, ...ccs]
								.filter((doc) => !doc.isDeleted)
								.map((doc) => (
									<HStack key={doc.fileId} alignItems="center" mb={1} justifyContent="flex-start" width="100%">
										<DocLink onClick={() => handleEnhancedDocClick(doc.fileId, doc.fileName)}>{doc.fileName}</DocLink>
									</HStack>
								))}
						</GridItem>
					)}

					{/* Additional Documents Section */}
					{additionalDocuments.filter((doc) => !doc.isDeleted).length > 0 && (
						<GridItem gridColumn="span 2">
							<Text fontWeight="600" color="gray.800" fontSize="16px" mb="8px" textAlign="left" width="100%">
								Proof of Full-Time School Enrollment
							</Text>
							{additionalDocuments
								.filter((doc) => !doc.isDeleted)
								.map((doc) => (
									<HStack key={doc.fileId} alignItems="center" mb={1} justifyContent="flex-start" width="100%">
										<DocLink onClick={() => handleEnhancedDocClick(doc.fileId, doc.fileName)}>{doc.fileName}</DocLink>
									</HStack>
								))}
						</GridItem>
					)}

					{/* Audit Documents Section */}
					{auditDocuments.filter((doc) => !doc.isDeleted).length > 0 && (
						<GridItem gridColumn="span 2">
							<Text fontWeight="600" color="gray.800" fontSize="16px" mb="8px" textAlign="left" width="100%">
								Audit Documents
							</Text>
							{auditDocuments
								.filter((doc) => !doc.isDeleted)
								.map((doc) => (
									<HStack key={doc.fileId} alignItems="center" mb={1} justifyContent="flex-start" width="100%">
										<DocLink onClick={() => handleEnhancedDocClick(doc.fileId, doc.fileName)}>{doc.fileName}</DocLink>
									</HStack>
								))}
						</GridItem>
					)}

					{/* Denied Documents Section */}
					{deniedDocuments.filter((doc) => !doc.isDeleted).length > 0 && !isProvider && (
						<GridItem gridColumn="span 2">
							<Text fontWeight="600" color="gray.800" fontSize="16px" mb="8px" textAlign="left" width="100%">
								Denied Documents
							</Text>
							{deniedDocuments
								.filter((doc) => !doc.isDeleted)
								.map((doc) => (
									<HStack key={doc.fileId} alignItems="center" mb={1} justifyContent="flex-start" width="100%">
										<DocLink onClick={() => handleEnhancedDocClick(doc.fileId, doc.fileName)}>{doc.fileName}</DocLink>
									</HStack>
								))}
						</GridItem>
					)}
				</Grid>
			</Box>

			{/* Edit Documents Modal */}
			<CustomModal
				isOpen={isOpen}
				title="Edit Documents"
				onCancel={onClose}
				onContinue={onClose}
				continueText="Done"
				cancelText={null}
				size="xl"
				content={
					<>
						{/* Enhanced Download Progress Display */}
						<FileOperationProgress operations={downloadOperations} operationType="download" />

						{/* Enhanced Upload Progress Display */}
						<FileOperationProgress operations={uploadOperations} operationType="upload" />

						{/* INVOICES SECTION */}
						<Box mb={6}>
							<Text fontWeight="600" fontSize="lg" mb={2}>
								Invoices
							</Text>

							{/* Invoice Upload Area with Existing Invoices */}
							<Box
								p={4}
								borderWidth="1px"
								borderRadius="md"
								borderStyle="dashed"
								borderColor="gray.300"
								bg="gray.50"
								{...getRootPropsInvoices()}>
								<input {...getInputPropsInvoices()} />

								{/* Existing Invoices */}
								{invoices.length > 0 && (
									<Box mb={4}>
										{invoices.map((doc) => (
											<HStack key={doc.fileId} alignItems="center" mb={1} width="100%">
												<DocLink
													onClick={() => handleEnhancedDocClick(doc.fileId, doc.fileName)}
													style={doc.isDeleted ? { color: "gray" } : {}}>
													{doc.fileName}
												</DocLink>
												{editable && (
													<DocumentButtons fileId={doc.fileId} isDeleted={doc.isDeleted} category="Invoices" />
												)}
											</HStack>
										))}
									</Box>
								)}

								<HStack>
									<SecondaryButton
										colorScheme="blackAlpha"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											openInvoices();
										}}
										isDisabled={!editable}>
										Upload Files
									</SecondaryButton>
									<Text color={isDragActiveInvoices ? "orange.600" : "gray"}>or drag & drop</Text>
								</HStack>
								<Text fontSize="xs" color="gray.500" mt={2}>
									Max file size: {formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)} | Max claim size:{" "}
									{formatFileSize(FILE_SIZE_LIMITS.MAX_CLAIM_SIZE)}
								</Text>
							</Box>
						</Box>

						{/* SUPPORTING DOCUMENTS SECTION */}
						<Box mb={6}>
							<Text fontWeight="600" fontSize="lg" mb={2}>
								Supporting Documents
							</Text>

							{/* Supporting Documents Upload Area with Existing Documents */}
							<Box
								p={4}
								borderWidth="1px"
								borderRadius="md"
								borderStyle="dashed"
								borderColor="gray.300"
								bg="gray.50"
								{...getRootPropsSupportingDocuments()}>
								<input {...getInputPropsSupportingDocuments()} />

								{/* Existing Supporting Documents */}
								{supportingDocuments.length > 0 && (
									<Box mb={4}>
										{supportingDocuments.map((doc) => (
											<HStack key={doc.fileId} alignItems="center" mb={1} width="100%">
												<DocLink
													onClick={() => handleEnhancedDocClick(doc.fileId, doc.fileName)}
													style={doc.isDeleted ? { color: "gray" } : {}}>
													{doc.fileName}
												</DocLink>
												{editable && (
													<DocumentButtons
														fileId={doc.fileId}
														isDeleted={doc.isDeleted}
														category="SupportingDocuments"
													/>
												)}
											</HStack>
										))}
									</Box>
								)}

								<HStack>
									<SecondaryButton
										colorScheme="blackAlpha"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											openSupportingDocuments();
										}}
										isDisabled={!editable}>
										Upload Files
									</SecondaryButton>
									<Text color={isDragActiveSupportingDocuments ? "orange.600" : "gray"}>or drag & drop</Text>
								</HStack>
								<Text fontSize="xs" color="gray.500" mt={2}>
									Max file size: {formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)} | Max claim size:{" "}
									{formatFileSize(FILE_SIZE_LIMITS.MAX_CLAIM_SIZE)}
								</Text>
							</Box>
						</Box>

						{/* CLAIM FORMS SECTION */}
						<Box mb={6}>
							<Text fontWeight="600" fontSize="lg" mb={2}>
								Claim Forms
							</Text>

							{/* Claim Forms Upload Area with Existing Documents */}
							<Box
								p={4}
								borderWidth="1px"
								borderRadius="md"
								borderStyle="dashed"
								borderColor="gray.300"
								bg="gray.50"
								{...getRootPropsClaimForms()}>
								<input {...getInputPropsClaimForms()} />

								{/* Existing Claim Forms and CCS */}
								{[...claimForms, ...ccs].length > 0 && (
									<Box mb={4}>
										{[...claimForms, ...ccs].map((doc) => (
											<HStack key={doc.fileId} alignItems="center" mb={1} width="100%">
												<DocLink
													onClick={() => handleEnhancedDocClick(doc.fileId, doc.fileName)}
													style={doc.isDeleted ? { color: "gray" } : {}}>
													{doc.fileName}
												</DocLink>
												{editable && !doc.isGenerated && (
													<DocumentButtons fileId={doc.fileId} isDeleted={doc.isDeleted} category="ClaimForms" />
												)}
											</HStack>
										))}
									</Box>
								)}

								<HStack>
									<SecondaryButton
										colorScheme="blackAlpha"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											openClaimForms();
										}}
										isDisabled={!editable}>
										Upload Files
									</SecondaryButton>
									<Text color={isDragActiveClaimForms ? "orange.600" : "gray"}>or drag & drop</Text>
								</HStack>
								<Text fontSize="xs" color="gray.500" mt={2}>
									Max file size: {formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)} | Max claim size:{" "}
									{formatFileSize(FILE_SIZE_LIMITS.MAX_CLAIM_SIZE)}
								</Text>
							</Box>
						</Box>

						{/* PROOF OF FULL-TIME SCHOOL ENROLLMENT SECTION */}
						<Box mb={6}>
							<Text fontWeight="600" fontSize="lg" mb={2}>
								Proof of Full-Time School Enrollment
							</Text>

							{/* Proof of Full-Time School Enrollment Upload Area with Existing Documents */}
							<Box
								p={4}
								borderWidth="1px"
								borderRadius="md"
								borderStyle="dashed"
								borderColor="gray.300"
								bg="gray.50"
								{...getRootPropsProofOfFullTimeSchool()}>
								<input {...getInputPropsProofOfFullTimeSchool()} />

								{/* Existing Proof of Full-Time School Enrollment Documents */}
								{additionalDocuments.length > 0 && (
									<Box mb={4}>
										{additionalDocuments.map((doc) => (
											<HStack key={doc.fileId} alignItems="center" mb={1} width="100%">
												<DocLink
													onClick={() => handleEnhancedDocClick(doc.fileId, doc.fileName)}
													style={doc.isDeleted ? { color: "gray" } : {}}>
													{doc.fileName}
												</DocLink>
												{editable && (
													<DocumentButtons
														fileId={doc.fileId}
														isDeleted={doc.isDeleted}
														category="AdditionalDocuments"
													/>
												)}
											</HStack>
										))}
									</Box>
								)}

								<HStack>
									<SecondaryButton
										colorScheme="blackAlpha"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											openProofOfFullTimeSchool();
										}}
										isDisabled={!editable}>
										Upload Files
									</SecondaryButton>
									<Text color={isDragActiveProofOfFullTimeSchool ? "orange.600" : "gray"}>or drag & drop</Text>
								</HStack>
								<Text fontSize="xs" color="gray.500" mt={2}>
									Max file size: {formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)} | Max claim size:{" "}
									{formatFileSize(FILE_SIZE_LIMITS.MAX_CLAIM_SIZE)}
								</Text>
							</Box>
						</Box>

						{(isHeldForAudit || auditDocuments.length > 0) && (
							<Box mb={6}>
								<Text fontWeight="600" fontSize="lg" mb={2}>
									Audit Documents
								</Text>
								<Box
									p={4}
									borderWidth="1px"
									borderRadius="md"
									borderStyle="dashed"
									borderColor="gray.300"
									bg="gray.50"
									{...getRootPropsAuditDocs()}>
									<input {...getInputPropsAuditDocs()} />
									{auditDocuments.length > 0 && (
										<Box mb={4}>
											{auditDocuments.map((doc) => (
												<HStack key={doc.fileId} alignItems="center" mb={1} width="100%">
													<DocLink
														onClick={() => handleEnhancedDocClick(doc.fileId, doc.fileName)}
														style={doc.isDeleted ? { color: "gray" } : {}}>
														{doc.fileName}
													</DocLink>
													{editable && (
														<DocumentButtons fileId={doc.fileId} isDeleted={doc.isDeleted} category="AuditDocuments" />
													)}
												</HStack>
											))}
										</Box>
									)}
									<HStack>
										<SecondaryButton
											colorScheme="blackAlpha"
											size="sm"
											onClick={(e) => {
												e.stopPropagation();
												openAuditDocs();
											}}
											isDisabled={!editable}>
											Upload Files
										</SecondaryButton>
										<Text color={isDragActiveAuditDocs ? "orange.600" : "gray"}>or drag & drop</Text>
									</HStack>
									<Text fontSize="xs" color="gray.500" mt={2}>
										Max file size: {formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)} | Max claim size:{" "}
										{formatFileSize(FILE_SIZE_LIMITS.MAX_CLAIM_SIZE)}
									</Text>
								</Box>
							</Box>
						)}

						{/* Denied Docs SECTION */}
						<Box mb={6}>
							<Text fontWeight="600" fontSize="lg" mb={2}>
								Denied Documents
							</Text>

							{/* Denied Docs Upload Area with Existing Documents */}
							<Box
								p={4}
								borderWidth="1px"
								borderRadius="md"
								borderStyle="dashed"
								borderColor="gray.300"
								bg="gray.50"
								{...getRootPropsDeniedDocs()}>
								<input {...getInputPropsDeniedDocs()} />

								{/* Existing Denied Documents */}
								{deniedDocuments.length > 0 && (
									<Box mb={4}>
										{deniedDocuments.map((doc) => (
											<HStack key={doc.fileId} alignItems="center" mb={1} width="100%">
												<DocLink
													onClick={() => handleEnhancedDocClick(doc.fileId, doc.fileName)}
													style={doc.isDeleted ? { color: "gray" } : {}}>
													{doc.fileName}
												</DocLink>
												{editable && (
													<DocumentButtons fileId={doc.fileId} isDeleted={doc.isDeleted} category="Denied" />
												)}
											</HStack>
										))}
									</Box>
								)}

								<HStack>
									<SecondaryButton
										colorScheme="blackAlpha"
										size="sm"
										onClick={(e) => {
											e.stopPropagation();
											openDeniedDocs();
										}}
										isDisabled={!editable}>
										Upload Files
									</SecondaryButton>
									<Text color={isDragActiveDeniedDocs ? "orange.600" : "gray"}>or drag & drop</Text>
								</HStack>
								<Text fontSize="xs" color="gray.500" mt={2}>
									Max file size: {formatFileSize(FILE_SIZE_LIMITS.MAX_FILE_SIZE)} | Max claim size:{" "}
									{formatFileSize(FILE_SIZE_LIMITS.MAX_CLAIM_SIZE)}
								</Text>
							</Box>
						</Box>
					</>
				}
			/>
		</>
	);
};

export default DataCardDisplayDocs;
